import type { ITrailerAttachment } from "../model/TrailerAttachment";
import type { IDeviceLink, TDeviceLinkType } from "../model/DeviceLink";
import type { DriveStore } from "../store/reducers";
import { FeatureFlagName } from "../utils/featureFlagName";

// MYG-148762: with DRIVE.ENABLE_TRAILER_DEVICE_LINK_API.RELEASE a trailer attachment is identified
// by device ids on both ends — attachment.device.id is the vehicle and attachment.trailer.id is the
// trailer's own Device.id (see mapDeviceToTrailer) — which is exactly how DeviceLink is keyed.
// The translation is therefore a plain field rename with no id lookup, and Device.tmpTrailerId
// (the gid column, which can be empty) is not consulted anywhere on this path.
// Trailer links are DeviceLinkType.Manual on the server.
export const TRAILER_DEVICE_LINK_TYPE: TDeviceLinkType = "Manual";

/**
 * The effective value, latched for the session on the first read that sees a loaded flag.
 *
 * The flag decides which identity (Trailer.Id vs trailer Device.Id) every trailer lookup, feed
 * version and defect-list map is keyed by, and this MR intentionally ships no migration between
 * the two. A mid-session Unleash refresh would therefore re-key live state underneath offline
 * attachments and DVIR feed versions, so the value is read once and frozen until the app restarts.
 * A read taken before the flags load is answered with the legacy behaviour and deliberately NOT
 * latched — the caches and several helpers run before the flags arrive, and latching there would
 * pin the whole session to the legacy identity no matter what the flag actually says.
 */
let latchedTrailerDeviceIdentity: boolean | undefined;

/**
 * The store the sync layer registered, used by the pure wire helpers below. They are called from
 * places that hold no store (translation functions, multiCall builders) and would otherwise have to
 * reach for the deprecated window.driveApp global.
 */
let registeredStore: DriveStore | undefined;

/** Test seam: drops the session latch and the registered store so a spec can exercise both flag states. */
export const resetTrailerDeviceIdentityLatch = (): void => {
    latchedTrailerDeviceIdentity = undefined;
    registeredStore = undefined;
};

/**
 * The flag's status, or `undefined` when the flags have not loaded yet.
 *
 * The distinction matters: `hasFeatureFlagLegacy` collapses "not loaded" and "disabled" into the
 * same `false`, and latching on that would pin the session to the legacy identity before the flag
 * had a chance to arrive. Reading the raw entry keeps the two apart, so an unloaded read is never
 * latched. The global is the last resort, for helpers such as mapDeviceToTrailer that run before
 * any sync has registered a store.
 */
const readTrailerDeviceIdentityFlag = (store?: DriveStore): boolean | undefined => {
    const effectiveStore = store ?? registeredStore ?? window.driveApp?.store;
    // typeof-guarded rather than just optional-chained: a partially built store (and several test
    // doubles) has no getState, and this read sits on the driver-facing inspection path where a
    // throw is exactly the failure this ticket exists to remove.
    if (typeof effectiveStore?.getState !== "function") {
        return undefined;
    }
    const flagStatus = effectiveStore.getState()?.data?.featureFlags?.[FeatureFlagName.ENABLE_TRAILER_DEVICE_LINK_API];
    return flagStatus ? !!flagStatus.isEnabled : undefined;
};

/**
 * The single seam every caller uses to read DRIVE.ENABLE_TRAILER_DEVICE_LINK_API.RELEASE.
 * Pass the store wherever one is available so the value comes from Redux rather than the
 * deprecated window.driveApp global.
 */
export function usesTrailerDeviceIdentity(store?: DriveStore): boolean {
    if (latchedTrailerDeviceIdentity !== undefined) {
        return latchedTrailerDeviceIdentity;
    }
    const value = readTrailerDeviceIdentityFlag(store);
    if (value === undefined) {
        return false;
    }
    latchedTrailerDeviceIdentity = value;
    return value;
}

/**
 * Called by each sync that owns a store, so every later read — including the storeless wire helpers —
 * resolves the flag from Redux. Also takes the first opportunity to latch the value for the session.
 */
export const primeTrailerDeviceIdentity = (store: DriveStore): void => {
    registeredStore = store;
    usesTrailerDeviceIdentity(store);
};

export interface ITrailerAttachmentSearch {
    deviceSearch?: IIdEntity;
    trailerSearch?: IIdEntity;
    activeFrom?: string | Date;
    activeTo?: string | Date;
}

export type TTrailerAttachmentGetCall = ["Get", { typeName: string; search: object }];


/** Returns null for a link missing either endpoint rather than throwing out of a surrounding map. */
export function fromDeviceLinkEntity(link: IDeviceLink): ITrailerAttachment | null {
    const trailerId = link?.sourceDevice?.id;
    const vehicleId = link?.destinationDevice?.id;
    if (!trailerId || !vehicleId) {
        return null;
    }
    return {
        id: link.id,
        device: { id: vehicleId },
        trailer: { id: trailerId },
        activeFrom: link.fromDate,
        activeTo: link.toDate,
        version: link.version
    } as ITrailerAttachment;
}

/** Maps DeviceLink rows to trailer attachments, dropping any row missing an endpoint. */
export function fromDeviceLinkEntities(links: IDeviceLink[] | null | undefined): ITrailerAttachment[] {
    return (links || [])
        .map(fromDeviceLinkEntity)
        .filter((attachment): attachment is ITrailerAttachment => !!attachment);
}

/**
 * A read of trailer attachments: the call to issue, paired with the decoder for its response.
 *
 * Encode and decode must come from the same branch. Reading the flag again when the response lands
 * can give a different answer — a call issued before the flags load is answered legacy and
 * deliberately not latched, and the flags can arrive while that request is in flight — which would
 * decode a TrailerAttachment payload as a DeviceLink.
 */
export interface ITrailerAttachmentRead {
    call: TTrailerAttachmentGetCall;
    decode: (results: unknown[] | null | undefined) => ITrailerAttachment[];
}

/**
 * Builds the "Get" that reads trailer attachments, with its matching decoder.
 *
 * Under the flag the read goes to DeviceLink. The sides are NOT what the names suggest: DeviceLink
 * and TrailerAttachment are two views of the same VehicleVehicle row, and both bridges write
 * DeviceIdField from the vehicle and ivehiclelinkid from the trailer — so on a trailer attachment
 * the vehicle is the DESTINATION device and the trailer is the SOURCE device.
 */
export function toTrailerAttachmentRead(search: ITrailerAttachmentSearch): ITrailerAttachmentRead {
    if (!usesTrailerDeviceIdentity()) {
        return {
            call: ["Get", { typeName: "TrailerAttachment", search }],
            decode: results => (results || []) as ITrailerAttachment[]
        };
    }

    const deviceLinkSearch: Record<string, unknown> = { linkType: TRAILER_DEVICE_LINK_TYPE };
    search.deviceSearch && (deviceLinkSearch.destinationDeviceSearch = search.deviceSearch);
    search.trailerSearch && (deviceLinkSearch.sourceDeviceSearch = search.trailerSearch);
    search.activeFrom && (deviceLinkSearch.activeFrom = search.activeFrom);
    search.activeTo && (deviceLinkSearch.activeTo = search.activeTo);
    return {
        call: ["Get", { typeName: "DeviceLink", search: deviceLinkSearch }],
        decode: results => fromDeviceLinkEntities(results as IDeviceLink[] | null | undefined)
    };
}

/** The call half of {@link toTrailerAttachmentRead}, for callers that only build a multiCall batch. */
export function toTrailerAttachmentGetCall(search: ITrailerAttachmentSearch): TTrailerAttachmentGetCall {
    return toTrailerAttachmentRead(search).call;
}

export function toDeviceLinkEntity(attachment: ITrailerAttachment): IDeviceLink {
    return {
        id: attachment.id,
        // The trailer is the source and the vehicle the destination — see toTrailerAttachmentRead.
        sourceDevice: { id: attachment.trailer?.id },
        destinationDevice: { id: attachment.device?.id },
        fromDate: attachment.activeFrom,
        toDate: attachment.activeTo,
        linkType: TRAILER_DEVICE_LINK_TYPE,
        version: attachment.version
    } as IDeviceLink;
}

/**
 * Maps the results of a toTrailerAttachmentGetCall back into the TrailerAttachment shape.
 *
 * Prefer toTrailerAttachmentRead, whose decoder is pinned to the branch that built the request.
 * This remains for multiCall batches, where the whole batch is built and decoded in one tick.
 */
export function normalizeTrailerAttachmentResults(results: unknown[] | null | undefined): ITrailerAttachment[] {
    return usesTrailerDeviceIdentity()
        ? fromDeviceLinkEntities(results as IDeviceLink[] | null | undefined)
        : (results || []) as ITrailerAttachment[];
}

// ── DVIR ──────────────────────────────────────────────────────────────────────────────────────
// Locally a trailer DVIR is still a DVIRLog with `trailer` set (DVIRLog.getResourceType()). On the
// server, under the flag, a trailer is a Device, so the wire uses DVIRLog.device / deviceSearch for
// it — the same way the Inspections module already inspects an attached trailer as a Device.

const NO_DEVICE_ID = "NoDeviceId";
const NO_TRAILER_ID = "NoTrailerId";

export interface IDvirAssetRef {
    device?: IIdEntity;
    trailer?: IIdEntity;
}

export function isTrailerDvirRef(log: IDvirAssetRef): boolean {
    return !!log.trailer?.id && log.trailer.id !== NO_TRAILER_ID;
}

/**
 * Asset search for a DVIRLog request: deviceSearch for a vehicle, and for a trailer under the flag;
 * trailerSearch otherwise. A log with neither asset yields an empty search rather than throwing —
 * this helper is called while building multiCall batches, where a TypeError would abort the batch.
 */
export function toDvirAssetSearch(log: IDvirAssetRef): { deviceSearch?: IIdEntity; trailerSearch?: IIdEntity } {
    if (isTrailerDvirRef(log)) {
        const id = (log.trailer as IIdEntity).id;
        return usesTrailerDeviceIdentity() ? { deviceSearch: { id } } : { trailerSearch: { id } };
    }
    const deviceId = log.device?.id;
    return deviceId ? { deviceSearch: { id: deviceId } } : {};
}

/** Outgoing DVIRLog: under the flag a trailer DVIR is sent with device = trailer Device and no trailer. */
export function toDvirLogWireEntity<T extends IDvirAssetRef>(log: T): T {
    if (!usesTrailerDeviceIdentity() || !isTrailerDvirRef(log)) {
        return log;
    }
    return { ...log, device: { id: (log.trailer as IIdEntity).id }, trailer: undefined };
}

/**
 * Incoming DVIRLog: under the flag a log whose device is a known trailer device is a trailer DVIR
 * locally, so it is re-keyed onto `trailer` (device reset to NoDeviceId, as legacy trailer logs have).
 *
 * `expectedTrailerId` is the authoritative answer for a by-id re-fetch of a log we already hold
 * locally (resetInvalidEdit, getDvirBeforeLog): the local copy knows it is a trailer inspection even
 * when the trailer has since been detached and evicted from the cache, which the `isTrailerDeviceId`
 * heuristic — the only option for feed results — would miss.
 *
 * The whole `device` payload is carried onto `trailer` (its id already is the trailer Device.id) so
 * that trailer.name / licensePlate keep rendering in the DVIR list and detail views, as they did on
 * the legacy path where the server populated DVIRLog.trailer.
 *
 * The server always rebuilds DVIRLog.trailer from the legacy Trailer id (the gid), and under the flag
 * it also populates DVIRLog.device with the trailer device. The device wins: an already set
 * `trailer` does not mean the log is in the local shape, only a NoDeviceId device does.
 */
export function fromTrailerDvirLogWire<T extends IDvirAssetRef>(
    log: T,
    isTrailerDeviceId: (deviceId: string) => boolean,
    expectedTrailerId?: string
): T {
    if (!usesTrailerDeviceIdentity() || !log) {
        return log;
    }
    const deviceId = log.device?.id;
    if (!deviceId || deviceId === NO_DEVICE_ID) {
        return log;
    }
    if (deviceId !== expectedTrailerId && !isTrailerDeviceId(deviceId)) {
        return log;
    }
    return { ...log, trailer: { ...log.device } as IIdEntity, device: { id: NO_DEVICE_ID } };
}
